import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  Button,
  Card,
  IconButton,
  List,
  ListItem,
  ListItemPrefix,
  Radio,
  Typography,
} from '@material-tailwind/react';
import { Fragment, useMemo, useState } from 'react';
import {
  ChosenVariantDto,
  CourseInfoType,
  ProductCourse,
} from '../../constants/types';
import { numberWithCommas } from '../../api/utils/format/numbers';
import { useParams, useSearchParams } from 'react-router-dom';

interface PlansProps {
  data: CourseInfoType;
  closeDrawerBottom: any;
  handleNext: () => void;
  handleSubmit: (
    childId: string,
    selectOptionData: ChosenVariantDto[],
  ) => Promise<void>;
  hasCourse: boolean;
}
const Plans = ({
  data,
  closeDrawerBottom,
  handleNext,
  handleSubmit,
  hasCourse,
}: PlansProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const optionVisible = searchParams.get('option') === 'true';

  /**
   * productSubType 추가 시 사용
   *
   * 변경 이력 2025-11-06 17:00:00
   *
   * as-is [ 아래 주석된 코드 내용]
   * 코스의 subType에 따라 구매하기 버튼 클릭 시 플랜 선택 후 옵션 선택을 띄우도록 되어있었음.
   * 코스중 소그룹만 true 였음. -> 소그룹만 교재를 추가해서 구매하게 되어있었음
   *
   * to-be
   * 모든 코스는 옵션 선택 창을 보지 않고 플랜에서 결제로 넘어감 -> 교재를 추가 구매 불가.
   *
  // const { productSubType } = useParams();
  // const optionVisible: boolean = useMemo(() => {
  //   if (option === 'true') {
  //     return true;
  //   } else {
  //     switch (productSubType) {
  //       case 'homeschool':
  //       case 'churchschool':
  //       case 'englishconversation':
  //         return false;
  //       case 'smallgroup':
  //         return true;
  //       default:
  //         return false;
  //     }
  //   }
  // }, [productSubType, option]);
  */

  const fitCourse = useMemo(() => {
    return (
      data.courses.find((course) => course.course.type === 'TEACHING') ||
      data.courses.find((course) => course.course.type === 'LEARNING') ||
      data.courses.find((course) => course.course.type === 'TRAINING')
    );
  }, [data]);

  const [radioItem, setRadioItem] = useState<ProductCourse>(fitCourse);
  const [isPurchaseLoading, setIsPurchaseLoading] = useState(false);

  const eventPurchaseHandle = async () => {
    setIsPurchaseLoading(true);
    try {
      await submitHandler();
    } finally {
      setIsPurchaseLoading(false);
    }
  };

  const onChangeDataHandler = (id: string) => {
    const plan = data.courses.find((plan) => plan.course.id === id);
    setRadioItem(plan);
  };

  const dayExpression = (day: number) => {
    if (day % 365 === 0) return `${day / 365} 년`;
    else return `${day} 일`;
  };

  const PlansDetailComp = ({ plan }: { plan: ProductCourse }) => {
    return (
      <label htmlFor={plan.course.id}>
        <ListItem className="flex w-full cursor-pointer items-center gap-3.5 py-3">
          <ListItemPrefix>
            <Radio
              id={plan.course.id}
              name="vertical-list"
              ripple={false}
              icon={
                <CheckIcon
                  stroke="white"
                  strokeWidth={2.5}
                  className="h-4 w-4"
                />
              }
              className="h-6 w-6 border-0 bg-purple5 opacity-50 checked:opacity-100 hover:before:opacity-0"
              containerProps={{
                className: 'p-0',
              }}
              defaultChecked={plan.course.id === radioItem?.course.id}
              onChange={() => {
                onChangeDataHandler(plan.course.id);
              }}
            />
          </ListItemPrefix>
          <div className="flex w-full flex-col items-end gap-1.5 sm:flex-row">
            <div className="mr-auto flex w-full flex-col gap-1.5">
              <Typography
                variant="h5"
                className="whitespace-wrap text-md font-medium leading-6 sm:text-lg"
              >
                {/* {`${fitCourse.course.name} [ ${dayExpression(
                  fitCourse.enroll_options[0].period
                )} ]`} */}
                {data.name}
              </Typography>
            </div>
            <div className="flex items-end gap-x-1">
              {data.origin_price != data.current_price && (
                <div className="text-xs text-gray7 line-through">
                  {numberWithCommas(data.origin_price)}
                </div>
              )}
              <Typography
                variant="h5"
                className="checked:text-text-purple5 text-md whitespace-nowrap font-semibold leading-7 sm:text-lg"
              >
                {numberWithCommas(data.current_price)} 원
              </Typography>
            </div>
          </div>
        </ListItem>
      </label>
    );
  };

  const submitHandler = async () => {
    searchParams.set('palnItems', 'true');
    setSearchParams(searchParams);
    await handleSubmit(data.id, []);
  };

  return (
    <Fragment>
      <section>
        <div className="mb-2.5 flex flex-row items-center gap-8">
          <div className="flex w-full flex-col items-center">
            <Typography variant="h5" className="self-start py-2.5 font-bold">
              플랜 선택
            </Typography>
            {/* <Typography variant="h6" className="font-normal self-start my-2.5">
                        플랜에 대한 설명 텍스트 입니다.
                        </Typography> */}
          </div>
          <div className="ml-auto flex items-center">
            <IconButton
              variant="text"
              color="blue-gray"
              onClick={() => closeDrawerBottom()}
            >
              <XMarkIcon className="!h-[30px] !w-[30px]" />
            </IconButton>
          </div>
        </div>
      </section>

      <section className="h-2/4 overflow-y-auto">
        <List className="flex flex-col gap-2.5">
          <Card className="mb-2.5" key={fitCourse.course.id}>
            <PlansDetailComp plan={fitCourse} />
          </Card>
        </List>
      </section>

      <section>
        {/* <div className="flex flex-col justify-center w-full bg-gray-100 rounded-[2px] overflow-hidden px-3 py-5 mb-2.5">
          <Typography variant="h6">[유의 사항 영역]</Typography>
          <Typography variant="h6">
            포인트는 적립 일로부터 3년간 유효합니다.
          </Typography>
        </div> */}
        <div className="flex w-full justify-center rounded-md bg-purple5">
          <Button
            disabled={hasCourse || !radioItem || isPurchaseLoading}
            variant="text"
            color="white"
            className="flex w-full flex-row items-center justify-center gap-2 whitespace-nowrap px-2.5 py-4 text-base font-bold leading-6"
            onClick={() =>
              optionVisible ? handleNext() : eventPurchaseHandle()
            }
          >
            {isPurchaseLoading && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            {hasCourse ? '이용 중' : optionVisible ? '다음' : '구매하기'}
          </Button>
        </div>
      </section>
    </Fragment>
  );
};

export default Plans;
